import { schema } from 'normalizr';
import _get from 'lodash/get';
import _values from 'lodash/values';
import { getOne, getList } from './modules/fetch';
import {
  createDenormalizeSelector,
  createAllEntitiesSelector,
  EntitiesAction,
  createListFromStateIdsSelector,
} from './modules/entities';
import { LogoutAction } from './modules/Auth';
import { CsvImport } from '@app/api';

export const CSV_IMPORT_ENTITY_NAME = 'csvImports';
export const csvImportLinkedEntities = [CSV_IMPORT_ENTITY_NAME];
export const csvImportSchema = new schema.Entity(CSV_IMPORT_ENTITY_NAME);

export const getCsvImport = (id: string) => {
  return getOne<CsvImport>({
    entity: CSV_IMPORT_ENTITY_NAME,
    id,
    schema: csvImportSchema,
  });
};

export const getCsvImportByOrgId = (orgId: string) => {
  return getList<CsvImport>({
    entity: CSV_IMPORT_ENTITY_NAME,
    path: `/organizations/${orgId}/csv-imports`,
    schema: new schema.Array(csvImportSchema),
  });
};

// reducers
interface CsvImportState {
  previouslyPendingImports: string[];
}

const initialState: CsvImportState = {
  // List of csvImports which have been pending during this navigation session
  previouslyPendingImports: [],
};

export const csvImportReducer = (
  state: CsvImportState = initialState,
  action: EntitiesAction | LogoutAction,
) => {
  if (action.type === 'AUTH.LOGOUT') {
    return initialState;
  }

  const csvImports: CsvImport[] = _values(
    _get(action, `meta.entities.${CSV_IMPORT_ENTITY_NAME}`, {}),
  );

  const previouslyPendingImports = csvImports.reduce(
    (current: string[], csvImport) => {
      if (csvImport.importFinishedAt) {
        return current;
      }
      if (current.includes(csvImport.id)) {
        return current;
      }
      return [...current, csvImport.id];
    },
    state.previouslyPendingImports,
  );

  return {
    ...state,
    previouslyPendingImports,
  };
};

export const CSV_IMPORT_MODULE_KEY: 'csvImports' = 'csvImports';
export type CsvImportGlobalState = { [CSV_IMPORT_MODULE_KEY]: CsvImportState };
type GlobalState = CsvImportGlobalState;

// Selectors

export const previouslyPendingImportsIdSelector = (state: GlobalState) =>
  state[CSV_IMPORT_MODULE_KEY].previouslyPendingImports;

export const csvImportByIdSelector = createDenormalizeSelector<CsvImport>(
  csvImportSchema,
  csvImportLinkedEntities,
);

export const allCsvImportSelector = createAllEntitiesSelector<CsvImport>(
  CSV_IMPORT_ENTITY_NAME,
  csvImportLinkedEntities,
  new schema.Array(csvImportSchema),
);

export const previouslyPendingImportsSelector = createListFromStateIdsSelector<
  GlobalState,
  CsvImport
>(
  new schema.Array(csvImportSchema),
  previouslyPendingImportsIdSelector,
  csvImportLinkedEntities,
);
